import React, { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import dayjs from 'dayjs'
import { Box, Button, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material'
import { ReactComponent as ArrowIcon } from '../../assets/images/arrowIcon.svg'
import FormLayout from '../forms/FormLayout'
import LabelErrorTAG from '../LabelErrorTAG'
import { selectStyle } from '../../constants/styleConstants'
import { IDateRangePicker } from './types'
import utils from './utils'
import { presets } from './constants'

const DateRangePicker: React.FC<IDateRangePicker> = ({
  isOpen = false,
  inputClickHandler,
  closeHandler,
  customContainerStyle = {},
  date: _date,
  setDate: _setDate,
  dateRange: _dateRange,
  setDateRange: _seDateRange,
  disablePresets = false,
  ref = undefined,
  label,
  name,
  rangePicker = false,
  disabled = false,
}) => {
  const [date, setDate] = useState<Date | null | undefined>(null)
  const [dateRange, setDateRange] = useState<(Date | null)[] | undefined>([null, null])
  const [selectedRangeId, setSelectedRangeId] = useState<string>('1')
  const [opened, setOpened] = useState<boolean>(isOpen)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const [rStartDate, rEndDate] = dateRange

  useEffect(() => {
    if (_date) {
      setDate(_date)
    }
    if (_dateRange) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setDateRange(_dateRange)
    }
  }, [_date, _dateRange])

  const onChange = (date: Date) => {
    _setDate && _setDate(date)
    setDate(date)
  }

  const dateRangeOnChange = (dates: [Date, Date]) => {
    setDateRange(dates)
    _seDateRange && _seDateRange(dates)
  }

  const rangeSelectOnChangeHandler = (event: SelectChangeEvent) => {
    // eslint-disable-next-line no-console
    setSelectedRangeId(event?.target?.value)
    const { start, end } = presets[event.target.value]
    setDateRange([start.toDate(), end.toDate()])
  }

  const handleCloseBtn = (e: React.SyntheticEvent) => {
    setOpened(false)
    closeHandler && closeHandler(e)
  }

  const handleOnInputClick = (): void => {
    setOpened(true)
    inputClickHandler && inputClickHandler()
  }

  const renderSelectOptions = () => {
    return Object.keys(presets).map((key, index) => {
      const { text, start } = presets[key]
      return (
        <MenuItem value={key} key={`${start.toDate()}-${index}`}>
          <Typography variant='bodyBold'>{text}</Typography>
        </MenuItem>
      )
    })
  }

  const MyContainer = ({ children }: { children: any }) => {
    return (
      <Box sx={{ ...customContainerStyle }}>
        <FormLayout>
          <div style={{ position: 'relative' }}>{children}</div>
          {!disablePresets && (
            <Box sx={{ display: 'flex', flexDirection: 'column ', gap: '.4rem' }}>
              <LabelErrorTAG label='Preset Ranges' />
              <Select
                onChange={rangeSelectOnChangeHandler}
                IconComponent={ArrowIcon}
                displayEmpty
                value={selectedRangeId}
                sx={[selectStyle]}
              >
                {renderSelectOptions()}
              </Select>
            </Box>
          )}

          <Box className='t-hbox t-gap-18'>
            <Button variant='outlined' onClick={handleCloseBtn}>
              <Typography variant='bodyRegular' color='inherit'>
                Close
              </Typography>
            </Button>
          </Box>
        </FormLayout>
      </Box>
    )
  }

  const render = rangePicker ? (
    <DatePicker
      open={opened}
      onInputClick={handleOnInputClick}
      onClickOutside={() => setOpened(false)}
      onBlur={() => setOpened(false)}
      onFocus={() => setOpened(true)}
      formatWeekDay={utils.handleFormateWeekDays}
      renderCustomHeader={utils.renderCustomHeader}
      calendarContainer={MyContainer}
      maxDate={dayjs().toDate()}
      name={name}
      showIcon
      disabled={disabled}
      wrapperClassName={'datePicker'}
      dateFormat={'EEE MMM dd yyyy'}
      selectsRange
      startDate={rStartDate}
      endDate={rEndDate}
      onChange={dateRangeOnChange}
    />
  ) : (
    <DatePicker
      open={opened}
      onInputClick={handleOnInputClick}
      onClickOutside={() => setOpened(false)}
      onBlur={() => setOpened(false)}
      onFocus={() => setOpened(true)}
      formatWeekDay={utils.handleFormateWeekDays}
      renderCustomHeader={utils.renderCustomHeader}
      calendarContainer={MyContainer}
      maxDate={dayjs().toDate()}
      name={name}
      disabled={disabled}
      showIcon
      wrapperClassName={'datePicker'}
      dateFormat={'EEE MMM dd yyyy'}
      selected={date}
      onChange={onChange}
    />
  )

  return (
    <Box className={`t-vbox t-date-control ${rangePicker ? 't-range-picker' : ''}`} ref={ref}>
      <LabelErrorTAG label={label} />
      {render}
    </Box>
  )
}

export default DateRangePicker
